import { useCallback, useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { toast } from "sonner";
import { LogOut, Search, Trash2, Mail, MailOpen, Inbox, RefreshCw } from "lucide-react";
import { IMG } from "@/data/media";
import { api, useAuth, formatError } from "@/admin/auth";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const FILTERS = [["all", "Tutti"], ["unread", "Non letti"], ["read", "Letti"]];
const LANG_LABEL = { it: "IT", fr: "FR", de: "DE", es: "ES" };
const fmt = (iso) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleString("it-IT", { dateStyle: "medium", timeStyle: "short" });
};

const MessageRow = ({ m, active, onOpen }) => (
  <button onClick={() => onOpen(m)} data-testid={`admin-message-row-${m.id}`} className={`w-full text-left p-4 rounded-2xl transition-colors duration-200 ${active ? "bg-[var(--green-pale)]" : "hover:bg-[var(--cream)]"}`}>
    <div className="flex items-center gap-2">
      {!m.read && <span className="w-2.5 h-2.5 rounded-full bg-[var(--coral)] shrink-0" aria-label="Non letto" />}
      <span className={`truncate ${m.read ? "font-medium" : "font-bold"}`}>{m.name}</span>
      <span className="ml-auto text-xs text-[var(--ink-soft)] shrink-0">{fmt(m.created_at)}</span>
    </div>
    <p className={`mt-1 truncate text-sm ${m.read ? "text-[var(--ink-soft)]" : "text-[var(--ink)] font-semibold"}`}>{m.subject}</p>
    <p className="mt-0.5 truncate text-sm text-[var(--ink-soft)]">{m.message}</p>
  </button>
);

const MessageDetail = ({ m, onToggle, onDelete, onBack }) => (
  <article className="p-6 sm:p-8" data-testid="admin-message-detail">
    <button onClick={onBack} className="lg:hidden mb-5 text-sm font-semibold text-[var(--green-deep)]" data-testid="admin-back-to-list">← Tutti i messaggi</button>
    <div className="flex flex-wrap items-start gap-3">
      <h2 className="text-3xl leading-tight flex-1 min-w-0 break-words" data-testid="admin-detail-subject">{m.subject}</h2>
      <span className="px-3 py-1 rounded-full bg-[var(--blue-pale)] text-xs font-bold">{LANG_LABEL[m.lang] || m.lang}</span>
    </div>
    <p className="mt-4 text-sm text-[var(--ink-soft)]">
      <span className="font-semibold text-[var(--ink)]">{m.name}</span> · <a href={`mailto:${m.email}?subject=${encodeURIComponent("Re: " + m.subject)}`} className="underline break-all" data-testid="admin-detail-email">{m.email}</a> · {fmt(m.created_at)}
    </p>
    <p className="mt-8 whitespace-pre-wrap leading-relaxed text-[var(--ink)]" data-testid="admin-detail-message">{m.message}</p>
    <p className="mt-6 text-xs text-[var(--ink-soft)]">Consenso privacy: {m.consent ? "sì" : "no"}</p>
    <div className="mt-8 flex flex-wrap gap-2">
      <a href={`mailto:${m.email}?subject=${encodeURIComponent("Re: " + m.subject)}`} className="btn btn-primary !py-2.5 !px-5 text-sm" data-testid="admin-reply-btn"><Mail size={15} /> Rispondi via email</a>
      <button onClick={() => onToggle(m)} className="btn btn-ghost !py-2.5 !px-5 text-sm" data-testid="admin-toggle-read-btn">{m.read ? <><Mail size={15} /> Segna come non letto</> : <><MailOpen size={15} /> Segna come letto</>}</button>
      <button onClick={() => onDelete(m)} className="btn !py-2.5 !px-5 text-sm bg-[var(--coral-pale)] text-[#7a1d14] hover:bg-[#f9d3cd]" data-testid="admin-delete-btn"><Trash2 size={15} /> Elimina</button>
    </div>
  </article>
);

export default function AdminMessages() {
  const { user, logout } = useAuth();
  const [data, setData] = useState({ items: [], total: 0, unread: 0 });
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [selected, setSelected] = useState(null);
  const [toDelete, setToDelete] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: res } = await api.get("/admin/messages", { params: { q, status } });
      setData({ items: Array.isArray(res?.items) ? res.items : [], total: res?.total ?? 0, unread: res?.unread ?? 0 });
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [q, status]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const setRead = async (m, read) => {
    await api.patch(`/admin/messages/${m.id}`, { read });
    setSelected((s) => (s && s.id === m.id ? { ...s, read } : s));
    load();
  };

  const open = (m) => {
    setSelected(m);
    if (!m.read) setRead(m, true).catch(() => toast.error("Impossibile aggiornare il messaggio"));
  };

  const toggle = (m) => setRead(m, !m.read).then(() => toast.success(m.read ? "Segnato come non letto" : "Segnato come letto")).catch(() => toast.error("Impossibile aggiornare il messaggio"));

  const confirmDelete = async () => {
    const m = toDelete;
    setToDelete(null);
    try {
      await api.delete(`/admin/messages/${m.id}`);
      toast.success("Messaggio eliminato");
      setSelected(null);
      load();
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
    }
  };

  return (
    <div className="min-h-screen bg-[var(--cream)]" data-testid="admin-messages-page">
      <Helmet><title>Messaggi — Area riservata MADE</title><meta name="robots" content="noindex, nofollow" /><html lang="it" /></Helmet>
      <header className="bg-white shadow-[0_1px_0_var(--line)]">
        <div className="wrap h-20 flex items-center gap-4">
          <img src={IMG.logo} alt="Logo MADE" className="h-11 w-auto" />
          <div className="leading-tight">
            <p className="font-display text-xl">Area riservata</p>
            <p className="text-xs text-[var(--ink-soft)] truncate max-w-[45vw]" data-testid="admin-user-email">{user?.email}</p>
          </div>
          <button onClick={logout} className="ml-auto btn btn-ghost !py-2.5 !px-4 text-sm" data-testid="admin-logout-btn"><LogOut size={15} /> Esci</button>
        </div>
      </header>
      <main className="wrap py-8 sm:py-12">
        <div className="flex flex-wrap items-end gap-4">
          <div>
            <h1 className="text-4xl sm:text-5xl">Messaggi</h1>
            <p className="mt-2 text-[var(--ink-soft)]" data-testid="admin-counts"><span className="font-semibold text-[var(--ink)]">{data.unread}</span> non letti su {data.total}</p>
          </div>
          <button onClick={load} className="ml-auto h-11 w-11 grid place-items-center rounded-full bg-white shadow-[0_0_0_1px_var(--line)]" aria-label="Aggiorna" data-testid="admin-refresh-btn"><RefreshCw size={17} className={loading ? "animate-spin" : ""} /></button>
        </div>
        <div className="mt-6 flex flex-col sm:flex-row gap-3">
          <label className="relative flex-1">
            <span className="sr-only">Cerca</span>
            <Search size={17} className="absolute left-4 top-1/2 -translate-y-1/2 text-[var(--ink-soft)]" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca per nome, email, oggetto o testo" className="w-full rounded-full bg-white pl-11 pr-4 py-3 outline-none shadow-[inset_0_0_0_1.5px_var(--line)] focus:shadow-[inset_0_0_0_2px_var(--green-deep)]" data-testid="admin-search-input" />
          </label>
          <div className="flex gap-1 bg-white rounded-full p-1 shadow-[inset_0_0_0_1px_var(--line)] self-start">
            {FILTERS.map(([k, label]) => (
              <button key={k} onClick={() => setStatus(k)} aria-pressed={status === k} className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors ${status === k ? "bg-[var(--green-deep)] text-white" : "hover:bg-[var(--green-pale)]"}`} data-testid={`admin-filter-${k}`}>{label}</button>
            ))}
          </div>
        </div>
        <div className="mt-6 grid gap-4 lg:grid-cols-12">
          <section className={`lg:col-span-5 bg-white rounded-[2rem] p-2 shadow-[0_0_0_1px_var(--line)] lg:max-h-[70vh] lg:overflow-y-auto ${selected ? "hidden lg:block" : ""}`} data-lenis-prevent data-testid="admin-message-list">
            {data.items.length === 0 && !loading && (
              <div className="p-10 text-center text-[var(--ink-soft)]" data-testid="admin-empty-state"><Inbox className="mx-auto mb-3" /> Nessun messaggio</div>
            )}
            {data.items.map((m) => <MessageRow key={m.id} m={m} active={selected?.id === m.id} onOpen={open} />)}
          </section>
          <section className={`lg:col-span-7 bg-white rounded-[2rem] shadow-[0_0_0_1px_var(--line)] min-h-[300px] ${selected ? "" : "hidden lg:block"}`}>
            {selected ? (
              <MessageDetail m={selected} onToggle={toggle} onDelete={setToDelete} onBack={() => setSelected(null)} />
            ) : (
              <div className="h-full min-h-[300px] grid place-items-center text-[var(--ink-soft)] p-10 text-center">Seleziona un messaggio per leggerlo</div>
            )}
          </section>
        </div>
      </main>
      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent data-testid="admin-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare questo messaggio?</AlertDialogTitle>
            <AlertDialogDescription>Il messaggio di {toDelete?.name} verrà eliminato definitivamente.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="admin-delete-cancel">Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-[#b42318] hover:bg-[#912018]" data-testid="admin-delete-confirm">Elimina</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
